<template>
  <div class="giveaway">
    <div class="wrapper">
      <h3>{{ giveaway.name }}</h3>

      <Time v-if="!giveaway.winner?.user" :endDate="endDate" />
    </div>

    <div v-if="giveaway.winner?.user" class="user-side">
      <div class="avatar-wrapper" v-bind="$attrs">
        <div class="avatar winner">
          <img
            :src="giveaway.winner.user.avatar"
            :alt="giveaway.winner.user.name"
          />

          <div class="winner-badge">
            <span>WINNER</span>
          </div>
        </div>
      </div>

      <p>{{ giveaway.winner.user.name }}</p>
    </div>

    <Button
      v-else
      type="primary__white"
      class="giveaway-button"
      :disabled="giveaway.is_participant"
      @click="$router.push({ name: 'giveaways' })"
    >
      {{ buttonText }}
    </Button>

    <img
      class="hover-image"
      :src="giveaway.item.thumbnail"
      alt=""
      aria-hidden="true"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import Time from "@/components/home/mobile/Giveaways/Time.vue"
import giveawayMixin from "../../mixins/giveawayMixin"

export default defineComponent({
  name: "GiveawayItem",

  components: {
    Time,
  },

  mixins: [giveawayMixin],
})
</script>

<style lang="scss" scoped>
.giveaway {
  --card-height: 100px;

  position: relative;

  display: flex;
  align-items: center;

  width: 100%;
  min-width: 0;
  height: var(--card-height);
  min-height: var(--card-height);
  max-height: var(--card-height);

  padding: 16px;
  gap: 12px;

  overflow: hidden;

  background: var(--black-gradient);
  box-shadow: var(--black-gradient);
  border-radius: 18px;

  min-width: 180px;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  // Keep content above the decorative image.
  > * {
    position: relative;
    z-index: 2;
  }

  .wrapper {
    display: flex;
    flex: 1;
    min-width: 0;

    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    min-width: 180px;

    gap: 6px;

    h3 {
      max-width: 100%;
      margin: 0;

      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      font-family: "Montserrat";
      font-style: normal;
      font-weight: 700;
      font-size: 13px;
      line-height: 17px;
      letter-spacing: -0.3px;

      color: #fff;
    }
  }

  .giveaway-button {
    flex: 0 0 auto;

    width: 110px !important;
    min-width: 110px;
    height: 36px;
    min-height: 36px;

    padding: 0 10px;

    font-size: 12px;
    line-height: 14px;
    letter-spacing: -0.2px;

    z-index: 3;

    min-width: 96px;
    align-self: end;
    margin-right: 10px;
  }

  .user-side {
    flex: 0 0 auto;

    display: flex;
    align-items: center;
    gap: 8px;

    width: auto;

    .avatar-wrapper {
      position: relative;

      width: 40px;
      height: 40px;
      flex: 0 0 40px;

      .avatar {
        position: relative;

        width: 100%;
        height: 100%;

        background: rgba(255, 255, 255, 0.04);
        border-radius: 50%;

        &.winner {
          overflow: visible;

          // Gradient ring
          &::after {
            content: "";

            position: absolute;
            inset: -2px;

            z-index: 0;

            background: linear-gradient(135deg, #ffaa00 50.81%, #ff5e00 100%);

            border-radius: inherit;
          }
        }

        img {
          position: relative;

          display: block;

          width: 100%;
          height: 100%;

          z-index: 1;

          object-fit: cover;

          background: rgb(47 49 53);
          border-radius: 50%;
        }
      }

      .winner-badge {
        position: absolute;

        left: 50%;
        bottom: -7px;

        display: flex;
        align-items: center;
        justify-content: center;

        width: 64px;
        height: 16px;

        padding: 1px;

        transform: translateX(-50%);

        background: linear-gradient(135deg, #ffaa00 50.81%, #ff5e00 100%);

        border-radius: 5px;

        z-index: 3;

        &::before {
          content: "";

          position: absolute;
          inset: 1px;

          background: #212429;
          border-radius: 4px;
        }

        span {
          position: relative;
          z-index: 1;

          font-family: "Druk Text Wide Cyr";
          font-weight: 900;
          font-size: 9px;
          line-height: 10px;

          background: linear-gradient(135deg, #ffaa00 50.81%, #ff5e00 100%);

          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      }
    }

    p {
      max-width: 90px;
      margin: 0;

      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      font-family: "Montserrat";
      font-style: normal;
      font-weight: 600;
      font-size: 11px;
      line-height: 14px;

      color: #fff;
    }
  }

  .hover-image {
    position: absolute;

    width: 125px;
    height: 125px;

    right: 50%;
    top: -14px;

    object-fit: contain;

    transform: rotate(-9.27deg);

    opacity: 0.85;

    pointer-events: none;

    transition: transform 0.6s ease, opacity 0.3s ease;

    z-index: 0;
  }

  // Desktop/tablet hover.
  &:hover {
    .hover-image {
      transform: rotate(0deg) scale(1.25) translateX(-6px) translateY(-8px);
    }
  }

  // Mobile/touch devices don't have a meaningful hover state.
  @media (hover: none) {
    &:hover {
      .hover-image {
        transform: rotate(-9.27deg);
      }
    }
  }

  // Small phones.
  @media (max-width: 380px) {
    padding: 12px;
    gap: 8px;

    .wrapper {
      h3 {
        font-size: 12px;
        line-height: 16px;
      }
    }

    .giveaway-button {
      font-size: 11px;
    }

    .user-side {
      .avatar-wrapper {
        width: 36px;
        height: 36px;
        flex-basis: 36px;
      }

      p {
        max-width: 70px;
        font-size: 10px;
      }
    }

    .hover-image {
      width: 105px;
      height: 105px;
      right: 0;
      top: -5px;
    }
  }
}
</style>
